import React from "react";
import { EXCURSION_TOOLTIP_HOVER_OPEN_MS } from "../display/excursionTooltipHelpers";

export type UseExcursionCardTooltipStateParams = {
  excursionId: number;
  forceOpen?: boolean;
  forceClose?: boolean;
  suppressHoverOpen?: boolean;
  /** e.g. `[data-excursion-booking-status-cell]` — skip hover open / close when pointer is over matching descendants of the anchor */
  ignoreHoverClosestSelector?: string;
  useClickMode: boolean;
  exclusivePlanningTooltipExcursionId?: number | null;
  onExclusivePlanningTooltipIntent?: (excursionId: number) => void;
  onExclusivePlanningTooltipRelease?: (excursionId: number) => void;
};

export type UseExcursionCardTooltipStateResult = {
  tooltipContentId: string;
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  setAnchorRef: (node: HTMLElement | null) => void;
  closeTooltipAfterBookingAction: () => void;
  onTooltipMouseEnter: () => void;
  onTooltipMouseLeave: (e: React.MouseEvent) => void;
  triggerAriaProps: {
    "aria-describedby": string | undefined;
    "aria-expanded": boolean | undefined;
    "aria-controls": string | undefined;
  };
  onTriggerClick: (e: React.MouseEvent) => void;
  onTriggerMouseEnter: (e: React.MouseEvent) => void;
  onTriggerMouseLeave: (e: React.MouseEvent) => void;
  onTriggerMouseMove: (e: React.MouseEvent) => void;
};

export function useExcursionCardTooltipState({
  excursionId,
  forceOpen,
  forceClose,
  suppressHoverOpen,
  ignoreHoverClosestSelector,
  useClickMode,
  exclusivePlanningTooltipExcursionId = null,
  onExclusivePlanningTooltipIntent,
  onExclusivePlanningTooltipRelease,
}: UseExcursionCardTooltipStateParams): UseExcursionCardTooltipStateResult {
  const [openByHover, setOpenByHover] = React.useState(false);
  const openByHoverRef = React.useRef(false);
  React.useEffect(() => {
    openByHoverRef.current = openByHover;
  }, [openByHover]);
  const [openByClick, setOpenByClick] = React.useState(false);
  const [mouseInTooltip, setMouseInTooltip] = React.useState(false);
  const [dismissAfterBookingAction, setDismissAfterBookingAction] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const tooltipContentId = React.useId();
  const leaveTriggerTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverOpenDelayRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const mouseInTooltipRef = React.useRef(false);
  const suppressHoverOpenRef = React.useRef(!!suppressHoverOpen);
  suppressHoverOpenRef.current = !!suppressHoverOpen;
  const ignoreHoverClosestSelectorRef = React.useRef(ignoreHoverClosestSelector);
  ignoreHoverClosestSelectorRef.current = ignoreHoverClosestSelector;

  const lastPointerClientRef = React.useRef({ x: 0, y: 0 });
  const pointerSampleRef = React.useRef(false);

  const planningExclusiveEnabled = Boolean(onExclusivePlanningTooltipIntent);
  const exclusiveOk =
    !planningExclusiveEnabled || exclusivePlanningTooltipExcursionId === excursionId;
  const hoverDerivedOpen =
    !useClickMode && exclusiveOk && ((openByHover && !suppressHoverOpen) || mouseInTooltip);
  const clickDerivedOpen = useClickMode && exclusiveOk && openByClick;
  const isOpen =
    !forceClose &&
    !dismissAfterBookingAction &&
    (Boolean(forceOpen) || hoverDerivedOpen || clickDerivedOpen);

  const clearHoverOpenDelay = React.useCallback(() => {
    if (hoverOpenDelayRef.current) {
      clearTimeout(hoverOpenDelayRef.current);
      hoverOpenDelayRef.current = null;
    }
  }, []);

  const clearLeaveTimeout = React.useCallback(() => {
    if (leaveTriggerTimeoutRef.current) {
      clearTimeout(leaveTriggerTimeoutRef.current);
      leaveTriggerTimeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      pointerSampleRef.current = false;
      return undefined;
    }
    const onPtr = (e: PointerEvent) => {
      lastPointerClientRef.current = { x: e.clientX, y: e.clientY };
      pointerSampleRef.current = true;
    };
    window.addEventListener("pointermove", onPtr, true);
    window.addEventListener("pointerdown", onPtr, true);
    return () => {
      window.removeEventListener("pointermove", onPtr, true);
      window.removeEventListener("pointerdown", onPtr, true);
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (forceClose) {
      setOpenByHover(false);
      setOpenByClick(false);
      setMouseInTooltip(false);
      mouseInTooltipRef.current = false;
      setDismissAfterBookingAction(false);
      clearLeaveTimeout();
      clearHoverOpenDelay();
    }
  }, [forceClose, clearLeaveTimeout, clearHoverOpenDelay]);

  React.useEffect(() => {
    setOpenByClick(false);
    setOpenByHover(false);
    setMouseInTooltip(false);
    mouseInTooltipRef.current = false;
  }, [useClickMode]);

  React.useEffect(() => {
    if (!planningExclusiveEnabled) return;
    if (exclusivePlanningTooltipExcursionId !== excursionId) {
      setOpenByClick(false);
      setOpenByHover(false);
      setMouseInTooltip(false);
      mouseInTooltipRef.current = false;
    }
  }, [planningExclusiveEnabled, exclusivePlanningTooltipExcursionId, excursionId]);

  React.useEffect(() => {
    if (!planningExclusiveEnabled || forceOpen) return;
    if (exclusivePlanningTooltipExcursionId !== excursionId) return;
    if (openByHover || mouseInTooltip || openByClick) return;
    onExclusivePlanningTooltipRelease?.(excursionId);
  }, [
    planningExclusiveEnabled,
    forceOpen,
    exclusivePlanningTooltipExcursionId,
    excursionId,
    openByHover,
    mouseInTooltip,
    openByClick,
    onExclusivePlanningTooltipRelease,
  ]);

  React.useEffect(() => {
    if (!useClickMode || !isOpen) return undefined;
    const handlePointerDown = (e: PointerEvent) => {
      if (forceOpen) return;
      const el = e.target;
      if (!(el instanceof Node)) return;
      if (anchorEl?.contains(el)) return;
      if (el instanceof Element && el.closest?.("[data-excursion-tooltip]")) return;
      setOpenByClick(false);
      setMouseInTooltip(false);
      mouseInTooltipRef.current = false;
      onExclusivePlanningTooltipRelease?.(excursionId);
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [useClickMode, isOpen, forceOpen, anchorEl, excursionId, onExclusivePlanningTooltipRelease]);

  React.useEffect(() => {
    if (!useClickMode || !openByClick) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenByClick(false);
        onExclusivePlanningTooltipRelease?.(excursionId);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [useClickMode, openByClick, excursionId, onExclusivePlanningTooltipRelease]);

  const closeTooltipAfterBookingAction = React.useCallback(() => {
    clearLeaveTimeout();
    clearHoverOpenDelay();
    setOpenByHover(false);
    setOpenByClick(false);
    setMouseInTooltip(false);
    mouseInTooltipRef.current = false;
    setDismissAfterBookingAction(true);
    onExclusivePlanningTooltipRelease?.(excursionId);
    if (typeof document !== "undefined") {
      const a = document.activeElement;
      if (a instanceof HTMLElement && a.closest?.("[data-excursion-tooltip]")) {
        a.blur();
      }
    }
  }, [clearLeaveTimeout, clearHoverOpenDelay, excursionId, onExclusivePlanningTooltipRelease]);

  React.useEffect(
    () => () => {
      clearLeaveTimeout();
      clearHoverOpenDelay();
    },
    [clearLeaveTimeout, clearHoverOpenDelay],
  );

  const onTooltipMouseEnter = React.useCallback(() => {
    if (useClickMode) return;
    clearLeaveTimeout();
    mouseInTooltipRef.current = true;
    setMouseInTooltip(true);
  }, [useClickMode, clearLeaveTimeout]);

  const onTooltipMouseLeave = React.useCallback(
    (e: React.MouseEvent) => {
      if (useClickMode) return;
      const next = e.relatedTarget;
      const ign = ignoreHoverClosestSelectorRef.current;
      if (next instanceof Node && anchorEl?.contains(next)) {
        if (ign && next instanceof Element && next.closest?.(ign)) {
          mouseInTooltipRef.current = false;
          setMouseInTooltip(false);
          setOpenByHover(false);
          clearLeaveTimeout();
          clearHoverOpenDelay();
          onExclusivePlanningTooltipRelease?.(excursionId);

          return;
        }

        mouseInTooltipRef.current = false;
        setMouseInTooltip(false);
        return;
      }
      mouseInTooltipRef.current = false;
      setMouseInTooltip(false);
      setOpenByHover(false);
      clearLeaveTimeout();
      clearHoverOpenDelay();
      onExclusivePlanningTooltipRelease?.(excursionId);
    },
    [
      useClickMode,
      anchorEl,
      excursionId,
      clearLeaveTimeout,
      clearHoverOpenDelay,
      onExclusivePlanningTooltipRelease,
    ],
  );

  const setAnchorRef = React.useCallback((node: HTMLElement | null) => {
    setAnchorEl(node);
  }, []);

  const triggerAriaProps = React.useMemo(
    () => ({
      "aria-describedby": isOpen && !useClickMode ? tooltipContentId : undefined,
      "aria-expanded": useClickMode ? isOpen : undefined,
      "aria-controls": useClickMode ? tooltipContentId : undefined,
    }),
    [isOpen, useClickMode, tooltipContentId],
  );

  const onTriggerClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (useClickMode) {
        e.stopPropagation();
        if (suppressHoverOpen) return;
        setDismissAfterBookingAction(false);
        onExclusivePlanningTooltipIntent?.(excursionId);
        setOpenByClick((o) => !o);
      }
    },
    [useClickMode, suppressHoverOpen, excursionId, onExclusivePlanningTooltipIntent],
  );

  const onTriggerMouseEnter = React.useCallback(
    (e: React.MouseEvent) => {
      if (useClickMode) return;
      const ign = ignoreHoverClosestSelectorRef.current;
      const t = e.target;
      if (ign && t instanceof Element && t.closest(ign)) return;

      clearLeaveTimeout();
      clearHoverOpenDelay();
      setDismissAfterBookingAction(false);
      hoverOpenDelayRef.current = setTimeout(() => {
        hoverOpenDelayRef.current = null;
        if (suppressHoverOpenRef.current) return;
        onExclusivePlanningTooltipIntent?.(excursionId);
        setOpenByHover(true);
      }, EXCURSION_TOOLTIP_HOVER_OPEN_MS);
    },
    [
      useClickMode,
      clearLeaveTimeout,
      clearHoverOpenDelay,
      excursionId,
      onExclusivePlanningTooltipIntent,
    ],
  );

  const onTriggerMouseMove = React.useCallback(
    (e: React.MouseEvent) => {
      if (useClickMode) return;
      const ign = ignoreHoverClosestSelectorRef.current;
      const t = e.target;
      const inIgnore = Boolean(ign && t instanceof Element && t.closest(ign));

      if (inIgnore) {
        clearHoverOpenDelay();
        if (!mouseInTooltipRef.current) {
          setOpenByHover(false);
          onExclusivePlanningTooltipRelease?.(excursionId);
        }

        return;
      }

      if (
        hoverOpenDelayRef.current !== null ||
        mouseInTooltipRef.current ||
        openByHoverRef.current
      ) {
        return;
      }

      clearLeaveTimeout();
      clearHoverOpenDelay();
      setDismissAfterBookingAction(false);
      hoverOpenDelayRef.current = setTimeout(() => {
        hoverOpenDelayRef.current = null;
        if (suppressHoverOpenRef.current) return;
        onExclusivePlanningTooltipIntent?.(excursionId);
        setOpenByHover(true);
      }, EXCURSION_TOOLTIP_HOVER_OPEN_MS);
    },
    [
      useClickMode,
      clearLeaveTimeout,
      clearHoverOpenDelay,
      excursionId,
      onExclusivePlanningTooltipIntent,
      onExclusivePlanningTooltipRelease,
    ],
  );

  const onTriggerMouseLeave = React.useCallback(
    (e: React.MouseEvent) => {
      if (useClickMode) return;
      if (forceOpen) return;
      const next = e.relatedTarget;
      if (next instanceof Element && next.closest?.("[data-excursion-tooltip]")) {
        return;
      }
      clearHoverOpenDelay();
      leaveTriggerTimeoutRef.current = setTimeout(() => {
        leaveTriggerTimeoutRef.current = null;
        if (mouseInTooltipRef.current) return;
        setOpenByHover(false);
        onExclusivePlanningTooltipRelease?.(excursionId);
      }, 100);
    },
    [useClickMode, clearHoverOpenDelay, excursionId, forceOpen, onExclusivePlanningTooltipRelease],
  );

  return {
    tooltipContentId,
    isOpen,
    anchorEl,
    setAnchorRef,
    closeTooltipAfterBookingAction,
    onTooltipMouseEnter,
    onTooltipMouseLeave,
    triggerAriaProps,
    onTriggerClick,
    onTriggerMouseEnter,
    onTriggerMouseLeave,
    onTriggerMouseMove,
  };
}
